import type {
  KeywordDefinition,
  SchemaValidateFunction,
  AnySchemaObject,
} from 'ajv';
import { findFirstUriIssueRichText } from './find-first-uri-issue-rich-text';
import { getUriMessage } from '../../../utils/uri-utils/uri-utils';
import type { UriIssue } from '../../../types';

// Schema-level feature flags
interface RichTextSchemaConfig {
  enforceUriProtocols: boolean;
}

// Convert an internal issue (from a validator module) into an AJV error object.
// Keeps keyword definition lean and uniform.
function toAjvError(
  issue: UriIssue,
  parentSchema?: AnySchemaObject,
  ctx?: { instancePath?: string },
) {
  return {
    instancePath: ctx?.instancePath ?? '',
    schemaPath: `#/richTextValid/${issue.subKey}`,
    keyword: 'richTextValid',
    params: { reason: issue.reason, href: issue.href },
    parentSchema,
    message:
      getUriMessage(issue.reason, issue.href) || 'contains invalid rich text',
  };
}

// Core keyword validate function invoked by AJV.
// Returns true when content passes all enabled rich-text checks.

function buildValidateRichText(
  allowedProtocols: string[] | undefined,
): SchemaValidateFunction {
  const validate: SchemaValidateFunction = (
    schema: RichTextSchemaConfig,
    data,
    parentSchema,
    ctx,
  ) => {
    // no data is valid because required and allowEmpty should be used to enforce presence
    if (typeof data !== 'string' || !data) return true;
    if (schema.enforceUriProtocols) {
      const issue = findFirstUriIssueRichText(data, allowedProtocols);
      if (issue) {
        validate.errors = [toAjvError(issue, parentSchema, ctx)];
        return false;
      }
    }
    validate.errors = undefined;
    return true;
  };
  return validate;
}

// Rich text custom AJV keyword: validates aspects of HTML-ish content.
// Currently only performs link (anchor href) checks via uri-validation.
// Designed to scale: add more validation groups (images, styles, etc.) and
// merge their message maps into RICH_TEXT_MESSAGES.
export const buildValidRichTextKeyword = (
  allowedProtocols?: string[],
): KeywordDefinition => ({
  keyword: 'richTextValid',
  type: 'string',
  errors: true,
  metaSchema: {
    type: 'object',
    properties: { enforceUriProtocols: { type: 'boolean' } },
    required: ['enforceUriProtocols'],
    additionalProperties: false,
  },
  validate: buildValidateRichText(allowedProtocols),
});
